import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./StrongboxPaywall.module.scss"

/**
 * StrongboxPaywall (`/strongbox`) — an original, self-contained ONBOARDING
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — the oldest, most
 * trusted way to keep something private: put it in a box only you can open.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "A box that opens for one person." Right now your memories sit out in the
 * open — the same roll anyone who picks up your phone can thumb through, a
 * shared album, cloud sync, every app you handed photo access. Private Photo
 * Vault gives each memory a personal SAFE-DEPOSIT BOX: you slide the tray home,
 * the door seals, and from then on it opens for exactly one person — you
 * (Face ID / Touch ID / a private code). The onboarding truth told as the
 * ritual of putting something precious somewhere safe.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * The hero is a brass safe-deposit box, door swung open, a pull-out TRAY of
 * your photos resting OUT in the light. You PUSH THE TRAY HOME (a horizontal
 * slide-to-seat drag) — the photos glide into the box, the brass door swings
 * shut over them, the bolt turns a quarter, the engraved plate lights
 * "PRIVATE · opens only for you" and a Face-ID seal blooms. Pull it back out
 * (or tap) to see who could reach in while it's open. A slide-the-drawer-home
 * gesture — not a tap-to-gather (Fold), a ring-sweep (Aperture), a phone tilt
 * (Facedown), a press-and-hold, a flip, a zip or a dial.
 *
 * ── Divergence (vs. the 140+ prior concepts, incl. the security cluster) ────
 * Layout      : an institutional SAFE-DEPOSIT box in a wall of numbered boxes,
 *               and the PRICING IS presented as two of those boxes — not a
 *               hero-on-top / feature-bullets / CTA-below stack.
 * Interaction : SLIDE-THE-TRAY-HOME horizontal drag that seats and seals the
 *               box. New to the set. Replayable; auto-demos once.
 * Metaphor    : a bank SAFE-DEPOSIT box — the most understood symbol of "kept
 *               private, opens for one keyholder." Photos in the tray keep it
 *               unmistakably a photo vault.
 * Value frame : WHO CAN REACH IN / risk reversal + the endowment of doing the
 *               safeguarding yourself — not loss, urgency or scarcity.
 * Register    : dignified institutional trust — brushed brass, engraved plates,
 *               warm safe-room light. Calm and premium, not fearful or playful.
 * CTA         : "Seal my box" — yearly featured as "The Annual Box."
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every "who can reach in" surface named is real (an ordinary phone roll truly
 * sits open to whoever holds it, to shared albums, to cloud sync and to apps
 * granted photo access) and every safeguard named is a REAL Private Photo Vault
 * feature (only-you lock with Face ID / Touch ID / a code · encrypted Cloud
 * Vault · break-in reports · app disguise). No countdown, no fabricated
 * scarcity, NO data-loss threat — nothing is deleted and nothing leaves the
 * phone; sealing the box only decides who is allowed to open it. "Save 79%" is
 * exact arithmetic from live product data. Yearly is the featured, default,
 * most prominent price; monthly is small and secondary. The 7-day free trial is
 * the real trial. Renders on built-in debug product data with bundled photos —
 * no native injection, no external egress.
 */

// The memories going into the box — bundled, self-contained repo photos. They
// fan out on the tray while it's open and glide into the dark cavity as it seats.
const MEMORIES = [
  "/byheart/m05.jpg",
  "/byheart/m09.jpg",
  "/byheart/m02.jpg",
  "/byheart/m14.jpg",
  "/byheart/m11.jpg",
]

// Who can reach into an ordinary phone roll right now — real, everyday surfaces.
// Each loses its reach, one at a time, as the box seals. Honest framing.
const REACHERS = [
  "Anyone who picks up your phone",
  "A shared album",
  "Photo cloud sync",
  "Apps you gave photo access",
]

// What every private box includes — real Private Photo Vault safeguards.
// Engraved as a plate, distinct from a plain checklist.
const INCLUDED = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Opens for you only",
    line: "Face ID, Touch ID or a private code",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private backup, yours on every device",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in reports",
    line: "A silent snapshot of a wrong-code attempt",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the box behind an ordinary app",
  },
]

const StrongboxPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()
  const darkMode = state.initialData?.darkMode ?? false

  // Blend the safe-area insets into the vault room so it runs edge to edge.
  useEffect(() => {
    const top = darkMode ? "#15171e" : "#efe7d6"
    const bottom = darkMode ? "#0f1016" : "#e7ddc9"
    actions.setHeaderInsetColor(top)
    actions.setFooterInsetColor(bottom)
  }, [actions, darkMode])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── box state ───────────────────────────────────────────────────────────
  // s: 0 = tray out / box open (in the open), 1 = seated & sealed (opens for
  // you). A spring chases a target; the tray-slide drag and taps set that target.
  const [s, setS] = useState(0)
  const sRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)

  const sealed = s > 0.82

  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = sRef.current
    const k = target > cur ? 0.14 : 0.12
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0012) next = target
    sRef.current = next
    setS(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const springTo = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        if (raf.current != null) cancelAnimationFrame(raf.current)
        raf.current = null
        sRef.current = v
        setS(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, tick],
  )

  const toggle = useCallback(() => {
    userActed.current = true
    springTo(targetRef.current > 0.5 ? 0 : 1)
  }, [springTo])

  // ── slide-the-tray-home drag ──────────────────────────────────────────────
  // Dragging the tray inward (leftward, toward the box) seats it; ~140px of
  // travel seals it fully. Release latches to the nearer end for a clean rest.
  const trackRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)
  const lastX = useRef(0)
  const TRAVEL = 150 // px of drag for a full seat

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      dragging.current = true
      if (raf.current != null) {
        cancelAnimationFrame(raf.current)
        raf.current = null
      }
      sRef.current = s
      targetRef.current = s
      lastX.current = e.clientX
      try {
        ;(e.target as Element).setPointerCapture?.(e.pointerId)
      } catch {}
    },
    [s],
  )

  const onMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - lastX.current
    lastX.current = e.clientX
    // dragging left (negative dx) pushes the tray home / seals the box
    let next = sRef.current - dx / TRAVEL
    next = Math.max(0, Math.min(1, next))
    sRef.current = next
    targetRef.current = next
    setS(next)
  }, [])

  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    springTo(sRef.current > 0.5 ? 1 : 0)
  }, [springTo])

  // Auto-demo: hold the open "in the open" state for a beat so the risk reads,
  // then seat the tray so the payoff lands. Reduced-motion arrives sealed (calm).
  useEffect(() => {
    if (reduceMotion) {
      targetRef.current = 1
      sRef.current = 1
      setS(1)
      return
    }
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) springTo(1)
    }, 1150)
    return () => {
      window.clearTimeout(t)
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // How many everyday surfaces can still reach in (drops to 0 as it seals).
  // Honest, and it never claims anything is deleted.
  const openReach = Math.round(REACHERS.length * (1 - s))

  return (
    <div
      className={`${styles.root} ${sealed ? styles.isSealed : ""}`}
      style={{ ["--s" as any]: s }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <rect
                  x="3.5"
                  y="4.5"
                  width="17"
                  height="15"
                  rx="2.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3.1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M12 12v2.4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Welcome to Private Photo Vault
          </span>
          <h1 className={styles.title}>
            {sealed ? (
              <>
                Sealed. It opens for
                <br />
                <span className={styles.accent}>one person — you.</span>
              </>
            ) : (
              <>
                Your memories sit
                <br />
                <span className={styles.accentWarn}>out in the open.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            {sealed
              ? "Every memory now has a box that opens for exactly one keyholder. No shared album, no sync, no app can reach in."
              : "The same roll anyone can thumb through. Let's give your memories a box — and slide the tray home."}
          </p>
        </section>

        {/* ── the safe-deposit box (hero) ────────────────────────────────── */}
        <section className={styles.stage}>
          {/* live "who can reach in" readout */}
          <div
            className={`${styles.readout} ${sealed ? styles.readoutSealed : ""}`}
            aria-live="polite"
          >
            <span className={styles.readoutDot} aria-hidden="true" />
            {sealed ? (
              <>
                Opens for <b>only you</b>
              </>
            ) : (
              <>
                Open to <b>{openReach}</b> who can reach in
              </>
            )}
          </div>

          <div className={styles.boxWrap}>
            {/* wall of neighbouring boxes for institutional context */}
            <span className={styles.wall} aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className={styles.wallBox} />
              ))}
            </span>

            {/* the box: cavity + fanned tray + swinging brass door */}
            <div className={styles.box}>
              {/* recessed lit interior */}
              <span className={styles.cavity} aria-hidden="true">
                <span className={styles.cavityLight} />
                {/* the tray of memories, pulled out when open */}
                <span className={styles.tray}>
                  {MEMORIES.map((src, i) => (
                    <span
                      key={src}
                      className={styles.photo}
                      style={{
                        backgroundImage: `url(${src})`,
                        ["--i" as any]: i,
                        ["--n" as any]: MEMORIES.length,
                      }}
                    />
                  ))}
                </span>
                <span className={styles.cavityShade} />
              </span>

              {/* the brass door, hinged left, swings shut as the tray seats */}
              <span className={styles.door} aria-hidden="true">
                <span className={styles.doorFace}>
                  <span className={styles.plateNo}>No. 01</span>
                  <span className={styles.hinges}>
                    <span />
                    <span />
                    <span />
                  </span>
                  {/* the bolt / escutcheon that turns a quarter as it seals */}
                  <span className={styles.lockPlate}>
                    <span className={styles.bolt}>
                      <svg viewBox="0 0 24 24" width="20" height="20">
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          opacity="0.5"
                        />
                        <rect
                          x="10.6"
                          y="4"
                          width="2.8"
                          height="16"
                          rx="1.4"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    {/* the Face-ID "only you" seal that lights once sealed */}
                    <span className={styles.faceSeal}>
                      <svg viewBox="0 0 24 24" width="17" height="17">
                        <path
                          d="M4 8V6.5A2.5 2.5 0 0 1 6.5 4H8M16 4h1.5A2.5 2.5 0 0 1 20 6.5V8M20 16v1.5a2.5 2.5 0 0 1-2.5 2.5H16M8 20H6.5A2.5 2.5 0 0 1 4 17.5V16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9 9.4v1.2M15 9.4v1.2"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9.2 14.2s1.2 1.4 2.8 1.4 2.8-1.4 2.8-1.4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </span>
                  <span className={styles.engrave}>
                    PRIVATE · opens only for you
                  </span>
                </span>
                {/* the thin inner return edge, sells the swing depth */}
                <span className={styles.doorEdge} />
              </span>
            </div>

            {/* the tray handle you drag — the one gesture */}
            <div
              ref={trackRef}
              className={styles.track}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              role="button"
              tabIndex={0}
              aria-label={
                sealed
                  ? "Pull the tray back out to see who could reach in"
                  : "Slide the tray home to seal the box"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  toggle()
                }
              }}
            >
              <span className={styles.trackRail} aria-hidden="true" />
              <span className={styles.handle} aria-hidden="true">
                <span className={styles.handleGrip} />
                <span className={styles.handleLabel}>
                  {sealed ? "Sealed" : "Slide the tray home"}
                </span>
                <span className={styles.handleArrow}>
                  {sealed ? (
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path
                        d="M5 12.5l4.4 4.4L19 7.2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path
                        d="M15 6l-6 6 6 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </span>
            </div>
          </div>
        </section>

        {/* ── honest framing ─────────────────────────────────────────────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <circle
                cx="12"
                cy="12"
                r="8.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M8.5 12.4l2.4 2.4 4.6-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing leaves your phone and nothing is deleted — sealing the box
          only decides who is allowed to open it.
        </p>

        {/* ── who loses their reach as it seals (honest, secondary) ──────── */}
        <ul className={styles.reachers}>
          {REACHERS.map((r, i) => {
            const shut = s > (i + 0.5) / REACHERS.length
            return (
              <li
                key={r}
                className={`${styles.reacher} ${shut ? styles.shut : ""}`}
              >
                <span className={styles.reacherDot} aria-hidden="true">
                  {shut ? (
                    <svg viewBox="0 0 24 24" width="12" height="12">
                      <path
                        d="M5 12.5l4.2 4.2L19 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="12" height="12">
                      <path
                        d="M12 3v12M12 19v2"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </span>
                <span className={styles.reacherName}>{r}</span>
                <span className={styles.reacherState}>
                  {shut ? "shut out" : "can reach in"}
                </span>
              </li>
            )
          })}
        </ul>

        {/* ── what every box includes (real safeguards) ─────────────────── */}
        <div className={styles.included}>
          <div className={styles.includedHead}>
            <span>What every private box includes</span>
            <span className={styles.includedCount}>
              {INCLUDED.length} safeguards
            </span>
          </div>
          <div className={styles.includedGrid}>
            {INCLUDED.map((it) => (
              <div key={it.key} className={styles.incl}>
                <span className={styles.inclIcon} aria-hidden="true">
                  <it.Icon width={17} height={17} />
                </span>
                <span className={styles.inclName}>{it.name}</span>
                <span className={styles.inclLine}>{it.line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── pricing: two brass boxes — yearly featured, monthly small ──── */}
        <div className={styles.plansLabel}>Choose your box</div>
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                The Annual Box
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyFoot}>
              Premium · every safeguard, on for a full year
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or the monthly box</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Seal my box — ${trialDays}-day free trial`
              : "Seal my box"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default StrongboxPaywall
